import React, { useEffect, useState } from 'react';
import { Alert, Button, Card, CardBody, FormGroup, Input, Table } from 'reactstrap';
import { DocumentIndex } from '../../types/meta';
import { MetaState } from '../../contexts/MetaContext';
import styles from '../../styles/archiveui.module.css';

interface IndexMappingInterfaceProps {
  metadata: MetaState;
  mappableColumns: string[]; // CSV columns available for mapping (from CSVParseResult.mappableColumns)
  onMappingComplete: (mapping: Record<string, string>) => void;
  onCancel: () => void;
}

// Human-friendly labels for standard field keys
const STANDARD_LABELS: Record<string, string> = {
  uniqueId: 'Unique ID',
  firstName: 'First Name',
  lastName: 'Last Name',
  dateOfBirth: 'Date of Birth',
};

const findBestMatchingIndex = (label: string, indexes: DocumentIndex[]): DocumentIndex | null => {
  const labelLower = label.toLowerCase().replace(/\s+/g, '');
  let match = indexes.find((idx) => idx.name.toLowerCase().replace(/\s+/g, '') === labelLower);
  if (match) return match;
  match = indexes.find((idx) => {
    const idxLower = idx.name.toLowerCase().replace(/\s+/g, '');
    return idxLower.includes(labelLower) || labelLower.includes(idxLower);
  });
  if (match) return match;
  const words = label.toLowerCase().split(/\s+/);
  match = indexes.find((idx) => {
    const idxWords = idx.name.toLowerCase().split(/\s+/);
    return words.some((w) => idxWords.includes(w) && w.length > 2);
  });
  return match || null;
};

const IndexMappingInterface: React.FC<IndexMappingInterfaceProps> = ({ metadata, mappableColumns, onMappingComplete, onCancel }) => {
  const [mapping, setMapping] = useState<Record<string, string>>({});
  const [errors, setErrors] = useState<string[]>([]);

  useEffect(() => {
    setMapping((prev) => {
      const autoMapping: Record<string, string> = { ...prev };
      mappableColumns.forEach((col) => {
        // Only auto-map if the column has no mapping yet
        if (!prev[col]) {
          const label = STANDARD_LABELS[col] || col;
          const matchingIndex = findBestMatchingIndex(label, metadata.documentIndexes);
          if (matchingIndex) {
            autoMapping[col] = matchingIndex.id;
          }
        }
      });
      return autoMapping;
    });
  }, [metadata, mappableColumns]);

  const handleMappingChange = (col: string, indexId: string) => {
    setMapping((prev) => ({ ...prev, [col]: indexId }));
  };

  const validateMapping = (): boolean => {
    const validationErrors: string[] = [];
    const mappedIndexIds = Object.values(mapping).filter((id) => id);
    if (mappedIndexIds.length === 0) {
      validationErrors.push('Please map at least one CSV column to a document index');
    }
    const uniqueIndexIds = new Set(mappedIndexIds);
    if (mappedIndexIds.length !== uniqueIndexIds.size) {
      validationErrors.push('Each document index can only be mapped to one CSV column');
    }
    setErrors(validationErrors);
    return validationErrors.length === 0;
  };

  const handleConfirm = () => {
    if (validateMapping()) onMappingComplete(mapping);
  };

  const getIndexById = (indexId: string): DocumentIndex | undefined => metadata.documentIndexes.find((idx) => idx.id === indexId);

  const getMappedCount = () => Object.values(mapping).filter((id) => id).length;

  return (
    <Card style={{ marginTop: '1em', borderRadius: '.5em' }}>
      <CardBody className={styles.searchBarHeader}>
        <h5 style={{ textAlign: 'start', fontWeight: 500, marginTop: '.5em' }}>Map CSV Columns to Document Indexes</h5>
      </CardBody>
      <CardBody>
        <Alert color="info">
          <strong>Step 2 of 3:</strong> Map CSV columns to document indexes in your system. We&apos;ve attempted to auto-map based on similar names — please
          verify and adjust as needed. <strong>Note:</strong> Not all columns need to be mapped.
        </Alert>

        {errors.length > 0 && (
          <Alert color="danger">
            <strong>Please fix the following issues:</strong>
            <ul style={{ marginBottom: 0, marginTop: '0.5em' }}>
              {errors.map((error, idx) => (
                <li key={idx}>{error}</li>
              ))}
            </ul>
          </Alert>
        )}

        <div style={{ marginBottom: '1em' }}>
          <strong>Mapped:</strong> {getMappedCount()} of {mappableColumns.length} columns (at least 1 required)
        </div>

        <Table bordered style={{ backgroundColor: 'white' }}>
          <thead>
            <tr>
              <th style={{ width: '25%' }}>CSV Column</th>
              <th style={{ width: '35%' }}>Maps to Document Index</th>
              <th style={{ width: '10%' }}>Type</th>
            </tr>
          </thead>
          <tbody>
            {mappableColumns.map((col) => {
              const label = STANDARD_LABELS[col] || col;
              const selectedIndex = mapping[col] ? getIndexById(mapping[col]) : undefined;
              return (
                <tr key={col}>
                  <td>
                    <strong>{label}</strong>
                    {!STANDARD_LABELS[col] && <span style={{ fontSize: '0.8em', color: '#888', marginLeft: '6px' }}>(custom)</span>}
                  </td>
                  <td>
                    <FormGroup style={{ marginBottom: 0 }}>
                      <Input type="select" value={mapping[col] || ''} onChange={(e) => handleMappingChange(col, e.target.value)}>
                        <option value="">-- Select Document Index --</option>
                        {[...metadata.documentIndexes]
                          .sort((a, b) => a.name.localeCompare(b.name))
                          .map((index) => (
                            <option key={index.id} value={index.id}>
                              {index.name}
                            </option>
                          ))}
                      </Input>
                    </FormGroup>
                  </td>
                  <td>
                    {selectedIndex && (
                      <span style={{ fontSize: '0.85em', padding: '2px 6px', borderRadius: '3px', backgroundColor: '#e9ecef' }}>{selectedIndex.type}</span>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </Table>

        <div style={{ marginTop: '1em', display: 'flex', gap: '0.5em', justifyContent: 'flex-end' }}>
          <Button color="secondary" onClick={onCancel}>
            Cancel
          </Button>
          <Button className={styles.scrbBtnBlue} onClick={handleConfirm} disabled={getMappedCount() === 0}>
            Confirm Mapping
          </Button>
        </div>
      </CardBody>
    </Card>
  );
};

export default IndexMappingInterface;
